/**
 * Telling us anything, from wherever you are when you think of it.
 *
 * Faults, confusions, ideas and the occasional kind word all arrive the same way, because a
 * teacher who has to decide which channel a thought belongs in usually decides not to bother.
 *
 * The value of what comes back is almost entirely in the context, and almost none of it in
 * what someone can be bothered to type. So it asks for one sentence, offers a box for more,
 * and quietly attaches everything that actually reproduces a problem: the tenant, the route,
 * the build, the viewport, and the trail of clicks, calls and console errors that led here.
 * A screenshot of the screen as it stands is taken the moment the dialog opens, before
 * anything is covered up, and more can be attached.
 */

import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { api } from "../lib/api";
import { readTrail, record, startRecording } from "../lib/breadcrumbs";
import { bytesOf, captureScreen, shotFromFile, type Shot } from "../lib/screenshot";
import { useOptionalSession } from "../lib/session";
import { Btn, Modal, S } from "../design/kit";

type Kind = "bug" | "confusing" | "idea" | "praise";

/* The question changes with the kind, or "what happened?" is put to someone with an idea. */
const KINDS: { key: Kind; label: string; ask: string; hint: string }[] = [
  {
    key: "bug",
    label: "Something is broken",
    ask: "In one line, what went wrong?",
    hint: "What were you doing, and what happened instead?",
  },
  {
    key: "confusing",
    label: "Something is confusing",
    ask: "In one line, what was confusing?",
    hint: "What did you expect this screen to do?",
  },
  {
    key: "idea",
    label: "An idea",
    ask: "In one line, what would you like?",
    hint: "What would you like to be able to do?",
  },
  {
    key: "praise",
    label: "Something works well",
    ask: "In one line, what worked?",
    hint: "What worked, and what made it work?",
  },
];

const MAX_SHOTS = 6;

export function ReportIssue() {
  const session = useOptionalSession();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);

  useEffect(() => startRecording(), []);
  /* Every screen change is a breadcrumb, recorded whether or not anyone opens the widget. */
  useEffect(() => {
    record("route", pathname, document.title);
  }, [pathname]);

  /*
   * Staff only. The shell also serves students, and a reporting channel aimed at children —
   * one that takes a screenshot of whatever is on their screen — is a different question
   * from one aimed at the adults running the school, and not one to answer by accident.
   */
  const role = session?.user?.role;
  if (role !== "teacher" && role !== "admin" && role !== "owner") return null;

  return (
    <div data-report-widget="">
      <button type="button" className="util-tab" onClick={() => setOpen(true)}>
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
          <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5Z" />
        </svg>
        Give feedback
      </button>
      {open ? <ReportDialog onClose={() => setOpen(false)} /> : null}
    </div>
  );
}

function ReportDialog({ onClose }: { onClose: () => void }) {
  const { pathname } = useLocation();
  const [kind, setKind] = useState<Kind>("bug");
  const [summary, setSummary] = useState("");
  const [steps, setSteps] = useState("");
  const [shots, setShots] = useState<Shot[]>([]);
  const [capturing, setCapturing] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  const file = useRef<HTMLInputElement>(null);

  /*
   * Taken once, on open. The dialog is filtered out of the capture, but the screen behind it
   * is already dimmed by the overlay — so this runs before the first paint of either.
   */
  useEffect(() => {
    let live = true;
    captureScreen()
      .then((shot) => live && setShots([shot]))
      .catch(() => live && setError("The screenshot could not be taken. You can still send the report."))
      .finally(() => live && setCapturing(false));
    return () => {
      live = false;
    };
  }, []);

  const attach = async (files: FileList | null) => {
    if (!files?.length) return;
    setError(null);
    const room = MAX_SHOTS - shots.length;
    if (room <= 0) {
      setError(`That is the most we can send at once — ${MAX_SHOTS} images.`);
      return;
    }
    try {
      const added = await Promise.all([...files].slice(0, room).map(shotFromFile));
      setShots((s) => [...s, ...added]);
    } catch (e) {
      setError(e instanceof Error ? e.message : "That file could not be read");
    }
    if (file.current) file.current.value = "";
  };

  const send = async () => {
    if (summary.trim().length < 3) {
      setError("Tell us in a line what this is about.");
      return;
    }
    setSending(true);
    setError(null);
    try {
      await api.post("/reports", {
        kind,
        summary: summary.trim(),
        steps: steps.trim(),
        page: { url: window.location.href, route: pathname, title: document.title },
        client: {
          viewport: `${window.innerWidth}×${window.innerHeight}`,
          dpr: window.devicePixelRatio,
          userAgent: navigator.userAgent,
          language: navigator.language,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
        breadcrumbs: readTrail(),
        shots,
      });
      setSent(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : "That did not send. Try once more.");
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <Modal
        title="Thank you — that is with us"
        blurb="We have the screen, the page you were on and what led up to it."
        width={520}
        onClose={onClose}
        footer={
          <Btn kind="primary" onClick={onClose}>
            Done
          </Btn>
        }
      >
        <p style={{ ...S.prose, margin: 0, color: "var(--c-ink-2)" }}>
          You do not need to do anything else. If we need more, we will come back to you at the
          address you sign in with.
        </p>
      </Modal>
    );
  }

  const active = KINDS.find((k) => k.key === kind)!;
  const weight = shots.reduce((a, s) => a + bytesOf(s), 0);

  return (
    <Modal
      title="Give feedback"
      blurb="Anything at all — broken, confusing, or an idea. We attach the screen and what you did just before it, so you only need the first line."
      width={620}
      onClose={onClose}
      footer={
        <>
          <Btn kind="secondarySm" onClick={onClose}>
            Cancel
          </Btn>
          <Btn kind="primary" onClick={() => void send()} disabled={sending}>
            {sending ? "Sending…" : "Send"}
          </Btn>
        </>
      }
    >
      <div style={{ display: "grid", gap: "14px" }}>
        <div className="report-kinds" role="radiogroup" aria-label="What kind of thing is this">
          {KINDS.map((k) => (
            <button
              key={k.key}
              type="button"
              role="radio"
              aria-checked={kind === k.key}
              className="report-kind"
              onClick={() => setKind(k.key)}
            >
              {k.label}
            </button>
          ))}
        </div>

        <label style={{ display: "grid", gap: "5px" }}>
          <span style={{ ...S.small, fontWeight: 600 }}>{active.ask}</span>
          <input
            className="report-input"
            value={summary}
            maxLength={300}
            onChange={(e) => setSummary(e.target.value)}
            placeholder={active.hint}
            autoFocus
          />
        </label>

        <label style={{ display: "grid", gap: "5px" }}>
          <span style={{ ...S.small, fontWeight: 600 }}>
            Anything else? <span style={{ ...S.micro, fontWeight: 400 }}>Optional</span>
          </span>
          <textarea
            className="report-input report-textarea"
            value={steps}
            maxLength={4000}
            rows={4}
            onChange={(e) => setSteps(e.target.value)}
            placeholder="If it only happens sometimes, or for one class and not another, say so here."
          />
        </label>

        <div style={{ display: "grid", gap: "7px" }}>
          <div style={S.between}>
            <span style={{ ...S.small, fontWeight: 600 }}>
              Screenshots{" "}
              <span style={{ ...S.micro, fontWeight: 400 }}>
                {capturing ? "taking one now…" : `${shots.length} of ${MAX_SHOTS}`}
              </span>
            </span>
            <Btn
              kind="secondarySm"
              onClick={() => file.current?.click()}
              disabled={shots.length >= MAX_SHOTS}
            >
              Add an image
            </Btn>
          </div>
          <input
            ref={file}
            type="file"
            accept="image/*"
            multiple
            className="sr-only"
            onChange={(e) => void attach(e.target.files)}
          />
          {capturing ? (
            <div className="report-shots">
              <div className="report-shot report-shot--waiting" />
            </div>
          ) : shots.length ? (
            <div className="report-shots">
              {shots.map((s, i) => (
                <div key={i} className="report-shot">
                  <img src={s.dataUrl} alt={`Screenshot ${i + 1}`} />
                  <button
                    type="button"
                    className="report-shot__x"
                    aria-label={`Remove screenshot ${i + 1}`}
                    onClick={() => setShots((all) => all.filter((_, n) => n !== i))}
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <span style={{ ...S.micro, color: "var(--c-ink-muted)" }}>
              None attached. The report still carries the page and what you did.
            </span>
          )}
          {weight > 0 ? (
            <span style={{ ...S.micro, color: "var(--c-ink-muted)" }}>
              About {Math.max(1, Math.round(weight / 1000))}kB to send.
            </span>
          ) : null}
        </div>

        {error ? (
          <p className="report-error" role="alert">
            {error}
          </p>
        ) : null}
      </div>
    </Modal>
  );
}
